import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { NumericInput } from '@client/components/_ui/data-entry/NumericInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { buildNumericInputRules } from '@client/utils/validation/numeric';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Form, Row, Skeleton, Switch, Tooltip, Typography } from 'antd';
import type { Rule } from 'antd/es/form';
import { Decimal } from 'decimal.js';
import { CircleMinus, CircleQuestionMark, Plus } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/financials/$id/tax')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Tax',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <TaxConfiguration />
        </PageController>
    );
}

type TaxConfig = Pick<
    RouterInputs['productType']['updateProductType']['set'],
    'salesTaxRate' | 'useCarLimitLogic' | 'useMarginScheme' | 'invoices' | 'invoices'
>;

const FormItem = Form.Item<TaxConfig>;

function TaxConfiguration() {
    const { id } = Route.useParams();
    const { data, isPending: isPendingGetProductType } = trpc.productType.getProductType.useQuery({ id });
    const { mutate, isPending } = trpc.productType.updateProductType.useMutation();

    const [form] = Form.useForm<TaxConfig>();

    const useCarLimitLogic = Form.useWatch(({ useCarLimitLogic }) => useCarLimitLogic, form);
    const invoices = Form.useWatch(({ invoices }) => invoices, form);

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                salesTaxRate: data.salesTaxRate,
                useCarLimitLogic: data.useCarLimitLogic,
                invoices: data.CarLimitInvoices,
                useMarginScheme: data.useMarginScheme,
            });
        }
    }, [data]);

    async function validateOverlappingInvoices(rule: Rule) {
        if (!invoices) {
            return;
        }

        // @ts-expect-error Missing type in antd
        const currentIndex = +rule.field.split('.')[1];

        if (currentIndex === invoices.length - 1) {
            return;
        }

        const currentDateFrom = invoices[currentIndex]?.dateFrom;
        const currentDateTo = invoices[currentIndex]?.dateTo;

        const overlappingInvoice = invoices.find((item, i) => {
            const dateFrom = item?.dateFrom;
            const dateTo = item?.dateTo;

            if (!currentDateTo || !dateFrom || !dateTo || currentIndex === i) {
                return false;
            }

            return !(currentDateTo < dateFrom || dateTo < currentDateFrom);
        });

        if (overlappingInvoice) {
            throw new Error(`Date range overlaps with item number ${invoices.indexOf(overlappingInvoice) + 1}`);
        }
    }

    if (isPendingGetProductType) {
        return <Skeleton active />;
    }

    return (
        <Form
            form={form}
            layout="vertical"
            disabled={isPending}
            onFinish={(values) => {
                message.loading('Saving...');

                mutate(
                    {
                        id,
                        set: {
                            // NumericInput emits undefined when cleared; send null so the rate is actually cleared
                            salesTaxRate: values.salesTaxRate ?? null,
                            useCarLimitLogic: values.useCarLimitLogic,
                            invoices: values?.invoices,
                            useMarginScheme: values.useMarginScheme,
                        },
                    },
                    {
                        onSuccess() {
                            message.success('Saved.');
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            }}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card type="inner">
                        <FormItem
                            label="Sales tax rate (%)"
                            name="salesTaxRate"
                            className="mb-0"
                            rules={buildNumericInputRules({ valueType: 'decimal', min: new Decimal('0.01') })}
                        >
                            <NumericInput valueType="decimal" precision={2} addonAfter="%" />
                        </FormItem>
                    </Card>
                </Col>

                <Col span={24}>
                    <Card type="inner">
                        <FormItem
                            label="Use car limit logic"
                            name="useCarLimitLogic"
                            valuePropName="checked"
                            help={
                                <>
                                    Includes car limit logic in GST calculations for 'new for old' settlements where the
                                    replacement vehicle exceeds the Australian car limit threshold
                                </>
                            }
                            className="mb-16"
                        >
                            <Switch
                                onChange={(value) => {
                                    form.setFieldsValue({
                                        invoices: value
                                            ? [
                                                  {
                                                      dateFrom: undefined,
                                                      dateTo: undefined,
                                                      carLimitThreshold: undefined,
                                                  },
                                              ]
                                            : undefined,
                                    });
                                }}
                            />
                        </FormItem>

                        {useCarLimitLogic && (
                            <Card
                                type="inner"
                                className="[&_.ant-card-head-title]:flex [&_.ant-card-head-title]:items-center [&_.ant-card-head-title]:gap-1"
                                title={
                                    <>
                                        Invoice date range
                                        <Tooltip title="If a payment is for a new vehicle that is above this threshold, the GST logic will be different and will have an impact on the ITC amount for the transaction.">
                                            <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                        </Tooltip>
                                    </>
                                }
                            >
                                <Typography.Paragraph type="secondary">
                                    The Car Limit Threshold configured here will apply for payment items with an invoice
                                    date between these dates.
                                </Typography.Paragraph>

                                <Form.List name="invoices">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map(({ key, name, ...props }, i) => (
                                                <Row key={key} gutter={8}>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            name={[name, 'dateFrom']}
                                                            label="Date from"
                                                            rules={[
                                                                { required: true },
                                                                { validator: validateOverlappingInvoices },
                                                            ]}
                                                        >
                                                            <DatePicker
                                                                className="w-full"
                                                                maxDate={invoices?.[i]?.dateTo || undefined}
                                                                placeholder=""
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            name={[name, 'dateTo']}
                                                            label="Date to"
                                                            rules={[
                                                                { required: i !== fields.length - 1 },
                                                                { validator: validateOverlappingInvoices },
                                                            ]}
                                                        >
                                                            <DatePicker
                                                                className="w-full"
                                                                minDate={invoices?.[i]?.dateFrom || undefined}
                                                                placeholder=""
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item
                                                            label="Car limit threshold"
                                                            name={[name, 'carLimitThreshold']}
                                                            rules={buildCurrencyInputRules({
                                                                isRequired: true,
                                                                gtZero: true,
                                                            })}
                                                            {...props}
                                                        >
                                                            <CurrencyInput />
                                                        </Form.Item>
                                                    </Col>
                                                    {fields.length > 1 && (
                                                        <Col span={4} className="flex items-center">
                                                            <Tooltip title="Remove item">
                                                                <Button
                                                                    type="tertiary"
                                                                    className="mt-1"
                                                                    icon={<CircleMinus />}
                                                                    onClick={() => remove(name)}
                                                                />
                                                            </Tooltip>
                                                        </Col>
                                                    )}
                                                </Row>
                                            ))}
                                            <Row gutter={8}>
                                                <Col span={20}>
                                                    <Button
                                                        type="secondary"
                                                        onClick={() => {
                                                            if (!invoices) {
                                                                return;
                                                            }

                                                            if (!invoices[invoices.length - 1]?.dateTo) {
                                                                form.setFields([
                                                                    {
                                                                        name: [
                                                                            'invoices',
                                                                            invoices.length - 1,
                                                                            'dateTo',
                                                                        ],
                                                                        errors: [
                                                                            'This field is required to create a new car limit threshold',
                                                                        ],
                                                                    },
                                                                ]);

                                                                return;
                                                            }

                                                            add();
                                                        }}
                                                        icon={<Plus />}
                                                        block
                                                    >
                                                        New car limit threshold
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </Form.List>
                            </Card>
                        )}
                    </Card>
                </Col>

                <Col span={24}>
                    <Card type="inner">
                        <FormItem
                            label="Use margin scheme logic"
                            name="useMarginScheme"
                            valuePropName="checked"
                            help="Applies GST margin scheme treatment for eligible real property purchases in Australia"
                        >
                            <Switch />
                        </FormItem>
                    </Card>
                </Col>

                <Col span={24} className="flex justify-end">
                    <Button type="primary" htmlType="submit" loading={isPending}>
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}
